/** One account's postings with a running balance (M11-03, M11-06 drill-down). */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { amount, drCr } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { manilaToday, useAccounts } from '../../lib/queries';
import { AccountSelect } from '../../components/AccountSelect';
import { Alert, Card, EmptyState, FilterBar, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass } from '../../components/ui';
import { SOURCE_LABEL } from '../accounting/journalTypes';

interface Ledger {
  account: { id: string; code: string; name: string; type: string };
  opening: string;
  closing: string;
  rows: {
    entryId: string;
    number: string | null;
    date: string;
    source: string;
    status: string;
    narration: string;
    description: string;
    debit: string;
    credit: string;
    balance: string;
  }[];
}

export function GeneralLedgerPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const today = manilaToday();
  const accounts = useAccounts();
  const [accountId, setAccountId] = useState(params.get('account') ?? '');
  const [from, setFrom] = useState(params.get('from') ?? `${today.slice(0, 4)}-01-01`);
  const [to, setTo] = useState(params.get('to') ?? today);

  const ledger = useQuery({
    queryKey: ['ledger', accountId, from, to],
    queryFn: () => api<Ledger>(`/reports/general-ledger/${accountId}?from=${from}&to=${to}`),
    enabled: Boolean(accountId && from && to && from <= to),
  });

  const choose = (id: string): void => {
    setAccountId(id);
    setParams({ account: id, from, to }, { replace: true });
  };

  return (
    <>
      <PageHeader title="General Ledger" description="Pick an account to see every posting with its running balance." />
      <FilterBar>
        <div className="w-80 max-w-full">
          <AccountSelect accounts={accounts.data ?? []} value={accountId} onChange={choose} allowControl className={`${filterClass} w-full`} />
        </div>
        <label className="text-sm text-slate-600 dark:text-slate-300">From <input type="date" className={`${filterClass} ml-1`} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-sm text-slate-600 dark:text-slate-300">To <input type="date" className={`${filterClass} ml-1`} value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </FilterBar>
      <Card>
        {!accountId && <EmptyState title="Choose an account" />}
        {ledger.isLoading && <Skeleton className="h-64 w-full" />}
        {ledger.isError && <Alert tone="critical">Could not load the ledger.</Alert>}
        {ledger.data && (
          <>
            <div className="mb-4 flex flex-wrap gap-6 text-sm">
              <div><span className="text-slate-500">Opening</span> <Money className="font-semibold">{drCr(ledger.data.opening)}</Money></div>
              <div><span className="text-slate-500">Closing</span> <Money className="font-semibold">{drCr(ledger.data.closing)}</Money></div>
            </div>
            {ledger.data.rows.length === 0 ? (
              <EmptyState title="No postings in this range" />
            ) : (
              <TableWrap>
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    <Th>Date</Th>
                    <Th>Entry</Th>
                    <Th>Source</Th>
                    <Th>Particulars</Th>
                    <Th align="right">Debit</Th>
                    <Th align="right">Credit</Th>
                    <Th align="right">Balance</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {ledger.data.rows.map((r, i) => (
                    <tr key={`${r.entryId}-${i}`}>
                      <Td className="whitespace-nowrap">{shortDate(r.date)}</Td>
                      <Td className="whitespace-nowrap">
                        <Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/journals/${r.entryId}`}>{r.number}</Link>
                        {r.status === 'VOIDED' && <span className="ml-2"><StatusChip tone="critical">Voided</StatusChip></span>}
                      </Td>
                      <Td>{SOURCE_LABEL[r.source] ?? r.source}</Td>
                      <Td className="max-w-sm truncate">{r.description || r.narration || '—'}</Td>
                      <Td align="right"><Money>{amount(r.debit, true)}</Money></Td>
                      <Td align="right"><Money>{amount(r.credit, true)}</Money></Td>
                      <Td align="right"><Money>{drCr(r.balance)}</Money></Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </>
        )}
      </Card>
    </>
  );
}
